<script setup>
import { computed, watch, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Timeline from 'primevue/timeline';
import Paginator from 'primevue/paginator';
import ScrollPanel from 'primevue/scrollpanel';
import { useEventsStore } from '@/stores/events.js';
import { fancyDate } from '@/filters/fancyDate.js';

const props = defineProps({
  // Видимость диалога
  visible: { type: Boolean, default: false },

  // Тип сущности: work | facility | customer | contract | user
  target: { type: String, required: true },

  // ID сущности
  entityId: { type: [String, Number], required: true },

  // Название сущности для заголовка
  entityName: { type: String, default: '' },

  // Фильтры по типам событий (опционально)
  verbFilters: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:visible']);

const eventsStore = useEventsStore();

// Текущая страница
const currentPage = ref(1);

// Получаем события из store
const eventsData = computed(() => eventsStore.getEvents(props.target, props.entityId));
const events = computed(() => eventsData.value.results || []);
const totalRecords = computed(() => eventsData.value.count || 0);
const rows = computed(() => events.value?.length || 25);

// Загрузка страницы
const loadPage = async (page) => {
  currentPage.value = page;
  const filters = {};
  if (props.verbFilters.length > 0) {
    filters.verb = props.verbFilters;
  }
  await eventsStore.loadEvents(props.target, props.entityId, page, filters);
};

// Загружаем первую страницу при открытии диалога
watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    loadPage(1);
  }
});

// Перезагружаем при смене entityId или target
watch(() => [props.entityId, props.target], () => {
  if (props.visible) {
    loadPage(1);
  }
});

const onPage = (e) => {
  // Paginator отдаёт 0-based page; сервер ожидает 1-based
  loadPage(e.page + 1);
};

// Форматирование даты и времени
const formatDateTime = (iso) => {
  if (!iso) return '—';
  const dateText = fancyDate(iso);
  let timeText = '';
  try {
    const d = new Date(iso);
    timeText = d.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit' });
  } catch {
    // ignore
  }
  return `${dateText} ${timeText}`.trim();
};

const onUpdateVisible = (value) => {
  emit('update:visible', value);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="onUpdateVisible"
    :header="`История изменений${entityName ? ': ' + entityName : ''}`"
    :modal="true"
    :closable="true"
    :style="{ width: '50vw' }"
    :breakpoints="{ '1200px': '70vw', '768px': '90vw' }"
  >
    <div class="event-history-content">
      <ScrollPanel style="width: 100%; height: 70vh;">
        <Timeline :value="events" layout="vertical" class="event-timeline">
          <template #content="{ item }">
            <div class="event-item">
              <!-- Дата и время -->
              <div class="event-datetime">
                {{ formatDateTime(item?.created_at) }}
              </div>

              <!-- Актор (кто) -->
              <div class="event-actor" v-if="item?.actor">
                {{ item.actor }}
              </div>

              <!-- Описание (что сделано) -->
              <div class="event-summary" v-if="item?.summary">
                {{ item.summary }}
              </div>
            </div>
          </template>

          <template #marker="{ item }">
            <div class="event-marker">
              <i class="pi pi-circle-fill"></i>
            </div>
          </template>
        </Timeline>
      </ScrollPanel>

      <!-- Пагинация -->
      <Paginator
        v-if="totalRecords > rows"
        :rows="rows"
        :totalRecords="totalRecords"
        :first="(currentPage - 1) * rows"
        :page="currentPage - 1"
        :rowsPerPageOptions="[rows]"
        @page="onPage"
        template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="Показано {first}–{last} из {totalRecords}"
        class="mt-3"
      />
    </div>
  </Dialog>
</template>

<style scoped>
.event-history-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Timeline с вертикальной полосой слева */
:deep(.event-timeline) {
  padding-left: 1rem;
  padding-top: 0.7rem;
}

:deep(.p-timeline-event-opposite) {
  display: none;
}

:deep(.p-timeline-event-content) {
  padding-left: 1rem;
}

:deep(.p-timeline-event-separator) {
  flex: 0 0 auto;
  align-items: center;
  min-width: 0.8rem;
}

/* Уменьшаем вертикальную линию */
:deep(.p-timeline-event-connector) {
  width: 1px;
  background-color: #e5e7eb;
}

/* Маркер события */
.event-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0.8rem;
  height: 0.8rem;
  color: #0ea5e9;
  background-color: white;
  border-radius: 50%;
  z-index: 1;
}

.event-marker i {
  font-size: 0.7rem;
}

/* Содержимое события */
.event-item {
  padding: 0.5rem 0;
  font-size: 0.875rem; /* Маленький шрифт */
  line-height: 1.2;
}

.event-datetime {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.event-actor {
  font-size: 0.813rem;
  color: #374151;
  font-weight: 500;
  margin-bottom: 0.125rem;
}

.event-summary {
  font-size: 0.813rem;
  color: #1f2937;
}

/* Убираем отступы у последнего элемента */
:deep(.p-timeline-event:last-child .p-timeline-event-content) {
  padding-bottom: 0;
}

:deep(.p-scrollpanel-content) {
  padding-right: 1rem;
}
</style>
